import { ShieldCheck, Lock, Eye, AlertCircle, Loader2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'
import { useAppStore } from '@/store'
import { useSecurity } from '@/hooks/useApi'

const SECRETS = ['GITHUB_TOKEN', 'CF_API_TOKEN', 'MCP_AUTH_TOKEN', 'ANTHROPIC_API_KEY', 'OPENAI_API_KEY', 'INTERNAL_CALLER_TOKEN']

const SECURITY_CONTROLS = [
  { name: 'MCP Auth Gate', status: 'enforced', detail: 'Fails closed when MCP_AUTH_TOKEN unset' },
  { name: 'Tiered Tool Access', status: 'enforced', detail: 'Unknown tools default to Tier 3' },
  { name: 'AI Code Commit Block', status: 'enforced', detail: 'ai_patch and create_tool disabled' },
  { name: 'Request Body Limit', status: 'enforced', detail: '1MB Content-Length check' },
  { name: 'Inter-worker Auth', status: 'enforced', detail: 'X-CORA-Internal-Token on all calls' },
  { name: 'CORS Restriction', status: 'open', detail: 'Still using Access-Control-Allow-Origin: *' },
  { name: 'workers_dev Exposure', status: 'open', detail: 'workers_dev=true in wrangler.toml' },
  { name: 'OAuth Consent Screen', status: 'open', detail: 'Auto-redirects without user consent' },
]

export function SecurityPanel() {
  const resolvedTheme = useAppStore((s) => s.resolvedTheme)
  const { data, isLoading } = useSecurity()

  const threatData = data ? [
    { name: 'Today', value: data.threats_blocked.today, color: '#EF4444' },
    { name: 'This Week', value: data.threats_blocked.week, color: '#F59E0B' },
    { name: 'This Month', value: data.threats_blocked.month, color: '#06B6D4' },
  ] : []

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Threats Blocked
              {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data ? (
              <>
                <div className="h-48 w-full min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={threatData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2} dataKey="value">
                        {threatData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: resolvedTheme === 'dark' ? '#1E293B' : '#FFFFFF',
                          border: `1px solid ${resolvedTheme === 'dark' ? '#334155' : '#E2E8F0'}`,
                          borderRadius: '8px',
                          fontSize: '12px',
                        }}
                      />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center mt-2">
                  <span className="text-2xl font-bold text-text-primary">{data.threats_blocked.month}</span>
                  <span className="text-sm text-text-muted block">blocked this month</span>
                </div>
                <div className="mt-2 text-center">
                  <span className="text-xs text-text-muted">Token revocations: {data.token_revocations} | Lockdown: {data.lockdown_status}</span>
                </div>
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-sm text-text-muted">Loading security data...</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Auth & Secrets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {SECRETS.map((secret) => (
                <div key={secret} className="flex items-center justify-between p-2 rounded-lg hover:bg-surface-elevated/50">
                  <div className="flex items-center gap-2">
                    <Lock className="h-3 w-3 text-text-muted" />
                    <span className="text-xs font-mono text-text-primary">{secret}</span>
                  </div>
                  <Badge variant="success" className="text-[10px]">set</Badge>
                </div>
              ))}
            </div>
            {data && data.quarantine_entries.length > 0 && (
              <div className="mt-4">
                <span className="text-xs font-semibold text-text-secondary">Recent Quarantine Entries</span>
                <div className="space-y-1 mt-1">
                  {data.quarantine_entries.slice(0, 5).map((q) => (
                    <div key={q.id} className="text-[11px] text-text-muted p-1 rounded bg-danger/5">
                      {q.source_type}: {q.reason} — {new Date(q.created_at).toLocaleDateString()}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Security Controls
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            {SECURITY_CONTROLS.map((ctrl) => (
              <div key={ctrl.name} className="flex items-center justify-between p-2 rounded-lg hover:bg-surface-elevated/50">
                <div className="flex items-center gap-3">
                  {ctrl.status === 'enforced' ? (
                    <ShieldCheck className="h-4 w-4 text-success" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-warning" />
                  )}
                  <div>
                    <span className="text-sm font-medium text-text-primary">{ctrl.name}</span>
                    <span className="text-[11px] text-text-muted block">{ctrl.detail}</span>
                  </div>
                </div>
                <Badge variant={ctrl.status === 'enforced' ? 'success' : 'warning'}>
                  {ctrl.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
